// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Edge toolbar — floating toolbar when an edge is selected.
export default class extends Controller {
  static values = {
    edgeId: { type: String, default: "" }
  }

  connect() {
    // M6: visual chrome lives on the .ui-flow-edge-toolbar class in flow.css
    // as theme tokens — only functional inline styles here, no cssText blob
    // clobbering host styles.
    this.element.classList.add("ui-flow-edge-toolbar")
    this.element.style.position = "absolute"
    this.element.style.display = "none"
    this._onEdgeClick = (e) => this._handleEdgeClick(e)
    // Multi-canvas isolation (S5, wave-8 A1): the engine dispatches edgeclick
    // on its OWN canvas root (bubbling), so binding at document level let one
    // click reach EVERY canvas's toolbar — a phantom toolbar over the sibling
    // canvas, and on an id collision the wrong canvas's edge got deleted.
    // Bind on this toolbar's own canvas root when one exists: a sibling
    // canvas's events bubble to document but never THROUGH this root, so they
    // can no longer reach this toolbar. The standalone mount (no canvas
    // ancestor) keeps document-wide behavior. viewportchange/edgedelete stay
    // document-level (they carry no canvas routing problem — the toolbar
    // repositions/hides only for its own latched edgeId).
    this._edgeClickTarget = this._canvasEl() || document
    this._edgeClickTarget.addEventListener("ui--flow:edgeclick", this._onEdgeClick)
    // Track the edge while visible (React Flow parity): pan/zoom otherwise
    // leaves the toolbar floating where the edge used to be. Only reposition
    // when shown — a hidden toolbar has no meaningful anchor.
    this._onViewportChange = () => {
      if (this.edgeIdValue && this.element.style.display !== "none") this._positionOnEdge(this.edgeIdValue)
    }
    document.addEventListener("ui--flow:viewportchange", this._onViewportChange)
    // The toolbar must not outlive its edge: deletion from any path outside
    // this controller (keyboard, node cascade, host code, sync) would leave it
    // floating with a stale edgeId.
    this._onEdgeDelete = (e) => {
      if (e.detail?.edgeId === this.edgeIdValue && this.element.style.display !== "none") this.hide()
    }
    document.addEventListener("ui--flow:edgedelete", this._onEdgeDelete)
  }

  disconnect() {
    // Remove from the SAME target connect() captured — the canvas root for a
    // canvas-mounted toolbar, document for the standalone mount.
    ;(this._edgeClickTarget || document).removeEventListener("ui--flow:edgeclick", this._onEdgeClick)
    document.removeEventListener("ui--flow:viewportchange", this._onViewportChange)
    document.removeEventListener("ui--flow:edgedelete", this._onEdgeDelete)
  }

  _handleEdgeClick(event) {
    // Belt-and-braces origin guard (wave-8 A1): covers the document-bound
    // case (a toolbar that connected before its canvas ancestor existed) — an
    // edgeclick bubbling out of a DIFFERENT canvas must not move this
    // toolbar. Standalone (no canvasEl) still processes document-dispatched
    // events; a document-targeted synthetic dispatch carries no Element
    // target and passes too.
    if (event.target instanceof Element && this._canvasEl() && !this._canvasEl().contains(event.target)) return
    const edgeId = event.detail.edgeId
    this.edgeIdValue = edgeId
    this._positionOnEdge(edgeId)
    this.element.style.display = "flex"
  }

  // The engine sits at the toolbar's canvas-root ancestor (canvas_component
  // renders this wrapper inside the canvas, and the wrapper carries no outlet
  // attribute of its own).
  _flow() {
    const canvasEl = this._canvasEl()
    if (!canvasEl) return null
    try { return this.application.getControllerForElementAndIdentifier(canvasEl, "ui--flow") } catch (e) { return null }
  }

  _canvasEl() {
    return this.element.closest("[data-controller~='ui--flow']")
  }

  // Selector-safe interpolation (#10): raw ids with quotes/backslashes break
  // querySelector outright (SyntaxError), and a document-wide scan can
  // resolve ANOTHER canvas's edge on id collision (S5). Every [data-edge]
  // probe below scopes to this toolbar's canvas element when one exists and
  // escapes the value. Local mirror of flow_controller._escapeSelectorValue —
  // lib/flow/utils.js exports no shared helper, so the semantics are
  // duplicated here (double-quoted attribute values only need quote +
  // backslash fallbacks).
  _esc(value) {
    const raw = `${value ?? ""}`
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(raw)
    return raw.replace(/(["\\])/g, "\\$1")
  }

  _edgeLookupRoot() {
    return this._canvasEl() || document
  }

  _findEdge(edgeId) {
    return this._edgeLookupRoot().querySelector(`[data-edge="${this._esc(edgeId)}"]`)
  }

  _positionOnEdge(edgeId) {
    const edgeEl = this._findEdge(edgeId)
    if (!edgeEl) return
    const pathLength = edgeEl.getTotalLength?.() || 0
    if (pathLength <= 0) return
    const mid = edgeEl.getPointAtLength(pathLength / 2)
    // The old math added the svg's client rect (post-transform page coords) to
    // SVG user units (flow coords, pre-transform), so the toolbar landed
    // offset by the canvas's page position and drifted with pan/zoom. Convert
    // through the engine's own flowToScreen, then drop the canvas offset for
    // coordinates relative to the `position: relative` canvas root.
    const flow = this._flow()
    if (!flow) return
    const screen = flow.flowToScreen(mid.x, mid.y)
    const rect = flow.element.getBoundingClientRect()
    // Dock clear of the label pill: a labelled edge renders its pill AT the
    // midpoint, so the toolbar rides an extra 14px up to stay above it.
    const hasLabel = !!(edgeEl.dataset["ui-FlowEdgeLabelValue"] || "").trim()
    this.element.style.left = `${screen.x - rect.left}px`
    this.element.style.top = `${screen.y - rect.top - 40 - (hasLabel ? 14 : 0)}px`
  }

  deleteEdge() {
    if (this.edgeIdValue) {
      const flow = this._flow()
      if (flow) {
        // Same gate the engine's keyboard delete applies (dataset is the
        // authoritative edge-def projection): deletable:false refuses with
        // the blocked event instead of silently destroying a protected edge.
        const edgeEl = this._findEdge(this.edgeIdValue)
        if (edgeEl?.dataset["ui-FlowEdgeDeletableValue"] === "false") {
          flow.element.dispatchEvent(new CustomEvent("ui--flow:edgedeleteblocked", { detail: { edgeId: this.edgeIdValue }, bubbles: true }))
        } else {
          flow.removeEdge(this.edgeIdValue)
        }
      } else {
        // Split-brain guard: a canvas ancestor exists but the engine lookup
        // failed (transient race) — DOM-only removal here would leave the
        // edge in the map, resurrecting on the next snapshot/sync. Only the
        // truly standalone mount (no canvas ancestor) falls back to DOM.
        if (!this.element.closest("[data-controller~='ui--flow']")) {
          // Legacy fallback (toolbar mounted outside a canvas): DOM-only
          // removal at DOCUMENT scope (no canvas ancestor to scope to), with
          // the same escaping as the scoped lookups.
          document.dispatchEvent(new CustomEvent("ui--flow:edgedelete", { detail: { edgeId: this.edgeIdValue } }))
          this._findEdge(this.edgeIdValue)?.remove()
          document.querySelector(`[data-edge-interaction="${this._esc(this.edgeIdValue)}"]`)?.remove()
        }
      }
    }
    this.element.style.display = "none"
  }

  hide() { this.element.style.display = "none" }
}
